import { ArrowRight, ClipboardCheck, PackageSearch } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ImportDialog from "@/components/ImportDialog";
import ProductVisual from "@/components/ProductVisual";
import StatusBadge from "@/components/StatusBadge";
import { formatRupees } from "@/lib/returns";
import { useReturns } from "@/contexts/ReturnsContext";

export default function ReturnsPage() {
  const navigate = useNavigate();
  const { returns, importReturns } = useReturns();
  const existingOrderIds = new Set(returns.map((item) => item.order_id));
  const uninspected = returns.filter((item) => !item.inspection).length;

  return (
    <main className="page-shell" data-testid="returns-page">
      <section className="page-intro">
        <div>
          <p className="eyebrow">Return operations</p>
          <h1>Returns ready for a fair check.</h1>
          <p className="intro-copy">
            Import returns, use the same five checks each time, and leave proof for every issue.
          </p>
        </div>
        <ImportDialog existingOrderIds={existingOrderIds} onImport={importReturns} />
      </section>

      <section className="demo-strip" data-testid="demo-data-label">
        <span className="demo-dot" aria-hidden="true" />
        <strong>Demo data</strong>
        <span>Six sample returns are ready to inspect and review.</span>
      </section>

      <section className="table-panel" aria-label="Returns spreadsheet">
        <div className="table-panel-heading">
          <div>
            <h2>Returns spreadsheet</h2>
            <p>{returns.length} items · {uninspected} awaiting inspection</p>
          </div>
          <ClipboardCheck size={24} aria-hidden="true" />
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Barcode</th>
                <th>Product</th>
                <th>Category</th>
                <th>Variant</th>
                <th>MRP</th>
                <th>Return reason</th>
                <th>Inspection</th>
                <th><span className="sr-only">Action</span></th>
              </tr>
            </thead>
            <tbody>
              {returns.map((item) => (
                <tr key={item.order_id} data-testid={`return-row-${item.order_id}`}>
                  <td className="cell-code">{item.order_id}</td>
                  <td className="cell-code muted-cell">{item.barcode}</td>
                  <td>
                    <div className="table-product">
                      <ProductVisual item={item} size="small" />
                      <strong>{item.product_name}</strong>
                    </div>
                  </td>
                  <td>{item.category}</td>
                  <td>{item.variant}</td>
                  <td className="price-cell">{formatRupees(item.mrp)}</td>
                  <td className="reason-cell">{item.return_reason}</td>
                  <td>
                    <StatusBadge
                      disposition={item.inspection?.disposition}
                      status={item.inspection ? "Inspected" : "Uninspected"}
                    />
                  </td>
                  <td>
                    {!item.inspection ? (
                      <button
                        className="table-action"
                        type="button"
                        onClick={() => navigate(`/inspect/${item.order_id}`)}
                        data-testid={`inspect-${item.order_id}-button`}
                      >
                        Inspect <ArrowRight size={15} aria-hidden="true" />
                      </button>
                    ) : (
                      <span className="done-cell"><PackageSearch size={16} aria-hidden="true" /> Done</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}